<script>
import lazyLoadComponent from '@router/lazyload-component'
import {
  orderPaymentMethodFilter,
  paymentStatusMapFilter,
} from '@utils/filter-value-to-text'
import { formatHotelOrderStayRange } from '@utils/format-hotel-stay'
import HotelOrderStatusTag from '@/src/router/views/schedule/hotel/components/order/partials/HotelOrderStatusTag.vue'
import pageMixin, { DEFAULT_PAGE_SIZE } from '@src/mixins/pagination'
import { getHotelInfo } from '@/src/router/views/hotel/api'
import { mapGetters, mapMutations } from 'vuex'

export default {
  components: {
    BaseStateLabel: () => lazyLoadComponent(import('@components/BaseStateLabel')),
    HotelOrderStatusTag,
  },
  filters: {
    orderPaymentMethodFilter,
    paymentStatusMapFilter,
  },
  mixins: [pageMixin],
  data() {
    return {
      loadedOrders: [],
      tryingToGetOrder: false,
      loadingList: false,
      expandRowKeys: [],
      pageLimit: DEFAULT_PAGE_SIZE,
    }
  },
  computed: {
    ...mapGetters('hotel/setting', ['showCheckoutDate']),
    pageQuery() {
      return {
        page: this.currentPage,
        limit: this.pageLimit,
      }
    },
  },
  watch: {
    currentPage: {
      handler: 'tryToFetchList',
      immediate: true,
    },
  },
  async created() {
    await this.fetchHotelSetting()
  },
  methods: {
    ...mapMutations('hotel/setting', ['SET_SHOW_CHECKOUT_DATE']),
    async fetchHotelSetting() {
      try {
        const hotelInfo = await getHotelInfo()
        this.SET_SHOW_CHECKOUT_DATE(hotelInfo?.showCheckoutDate)
      } catch (error) {
        console.error('取得住宿設定失敗:', error)
        this.SET_SHOW_CHECKOUT_DATE(1)
      }
    },
    async tryToFetchList() {
      this.loadingList = true
      try {
        const { items, pagination } = await this.$store.dispatch('hotel/order/fetchMemberList', {
          memberId: this.$route.params.memberId,
          ...this.pageQuery,
        })
        this.loadedOrders = items || []
        this.$nextTick(() => {
          if (this.$refs.pagination) {
            this.$refs.pagination.updateTotal(pagination.total || 0)
            this.currentPageLimit()
          }
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.loadingList = false
      }
    },
    currentPageLimit() {
      try {
        const remainder = this.$refs.pagination.total % this.$refs.pagination.pageSize
        const qotient = this.$refs.pagination.total / this.$refs.pagination.pageSize
        const totalPage = remainder ? parseInt(qotient, 10) + 1 : parseInt(qotient, 10)
        if (this.currentPage > totalPage && totalPage > 0) {
          this.currentPage = totalPage
        }
      } catch (error) {
        console.error(error.message)
      }
    },
    onCellClick(row, column) {
      if (column.type === 'expand') return
      if (!row.productId || !row.containerId) return

      this.$router.push({
        name: 'hotelViewOrder',
        params: { productId: row.productId, containerId: row.containerId },
      })
    },
    handleExpand(row) {
      if (this.expandRowKeys[0] === row.containerId) {
        this.expandRowKeys = []
      } else {
        this.expandRowKeys = [row.containerId]
      }
    },
    getRowKeys(row) {
      return row.containerId
    },
    formatBookingDates(order) {
      return formatHotelOrderStayRange(order, this.showCheckoutDate)
    },
    getRoomTitles(bookings) {
      if (!bookings || bookings.length === 0) return ''
      const titles = [...new Set(bookings.map((b) => b.title))]
      return titles.join('、')
    },
    isOrderRefunded(row) {
      return Array.isArray(row.orders) && row.orders.some((o) => o.isRefunded === true)
    },
  },
}
</script>

<template>
  <div v-loading.fullscreen.lock="tryingToGetOrder" element-loading-text="取得訂單資訊中">
    <BasePageHeader>
      <template v-slot:title>
        <span style="vertical-align: middle;">住宿訂單</span>
      </template>
    </BasePageHeader>
    <div class="bg-white">
      <div class="el-table-wrapper">
        <el-table
          v-loading.lock="loadingList"
          class="isBgDark"
          element-loading-background="rgba(255, 255, 255, 0.6)"
          :data="loadedOrders"
          :default-sort="{ prop: 'created_at', order: 'descending' }"
          :row-key="getRowKeys"
          :expand-row-keys="expandRowKeys"
          @cell-click="onCellClick"
          @expand-change="handleExpand"
        >
          <el-table-column v-slot="{ row }" type="expand">
            <div :class="$style['order-expanded']">
              <div v-for="(order, index) in row.orders || []" :key="index" class="expanded-item">
                <div class="item">
                  <div>
                    <div class="Jost fs-sm fw-medium">{{ `#${order.displayId}` }}</div>
                    <div class="mt-1">
                      <HotelOrderStatusTag :status="order.status" />
                    </div>
                  </div>
                  <div>
                    <div>預約日期：{{ formatBookingDates(order) }}</div>
                    <div class="fs-2sm text-secondary-light">{{ getRoomTitles(order.bookings) }}</div>
                  </div>
                  <div>
                    <div class="Jost">${{ order.total }}</div>
                  </div>
                </div>
                <el-divider v-if="index < row.orders.length - 1"></el-divider>
              </div>
            </div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="訂單編號" prop="displayId" min-width="150">
            <div class="Jost fs-sm fw-medium">{{ `#${row.displayId}` }}</div>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款方式" prop="paymentMethod" min-width="150" sortable>
            <BaseStateLabel state="secondary-light" text>
              {{ row.paymentMethod | orderPaymentMethodFilter }}
            </BaseStateLabel>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="付款狀態" prop="paymentStatus" min-width="130">
            <BaseStateLabel :state="row.paymentStatus | paymentStatusMapFilter('color')" text>
              {{ row.paymentStatus | paymentStatusMapFilter('text') }}
              <span v-if="isOrderRefunded(row)" style="color: #E6A23C">(已退款)</span>
            </BaseStateLabel>
          </el-table-column>
          <el-table-column v-slot="{ row }" label="總金額" min-width="100">
            <div class="Jost">${{ row.total }}</div>
          </el-table-column>
          <el-table-column min-width="40">
            <el-tooltip content="查看訂單詳情" placement="top">
              <el-button type="text">
                <BaseIcon name="ellipsis-v" />
              </el-button>
            </el-tooltip>
          </el-table-column>
        </el-table>
      </div>
      <BasePagination ref="pagination" :page-size="pageLimit" :current-page.sync="currentPage" />
    </div>
  </div>
</template>

<style lang="scss" module>
.order-expanded {
  padding: 20px;
  :global {
    .expanded-item {
      .item {
        display: flex;
        align-items: center;
        padding: 10px 0;

        > div {
          flex: 1;
          &:first-child {
            flex-basis: 25%;
          }
          &:nth-child(2) {
            flex-basis: 50%;
          }
          &:last-child {
            flex-basis: 25%;
            text-align: right;
          }
        }
      }
    }
  }
}
</style>
